import React, { useState, useEffect, useCallback} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'react-hot-toast';
import { Loader2, ArrowLeft, RotateCcw, Package, CheckCircle2, FileText, DollarSign, Clock, XCircle, ReceiptText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReturnsOrderView = () => {
  const { returnsNumber } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [returns, setReturns] = useState(null);
  const [receiveQuantities, setReceiveQuantities] = useState({});
  const [approveQuantities, setApproveQuantities] = useState({});


  const loadReturns = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/otc/returns/${returnsNumber}`);
      setReturns(response.data);
      
      // Initialize quantities for actions
      const receiveQty = {};
      const approveQty = {};
      response.data.items?.forEach(item => {
        receiveQty[item.item_number] = item.return_quantity;
        approveQty[item.item_number] = item.received_quantity;
      });
      setReceiveQuantities(receiveQty);
      setApproveQuantities(approveQty);
    } catch (error) {
      console.error('Error loading returns order:', error);
      toast.error('Failed to load returns order');
    } finally {
      setIsLoading(false);
    }
  }, [returnsNumber]);

  useEffect(() => {
    loadReturns();
  }, [loadReturns]);

  const handleReceiveReturns = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/otc/returns/${returnsNumber}/receive`,
        receiveQuantities
      );
      setReturns(response.data);
      toast.success('Items received successfully');
    } catch (error) {
      console.error('Error receiving returns:', error);
      toast.error(error.response?.data?.detail || 'Failed to receive returns');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveForCredit = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/otc/returns/${returnsNumber}/approve`,
        approveQuantities
      );
      setReturns(response.data);
      toast.success('Items approved for credit');
    } catch (error) {
      console.error('Error approving returns:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve returns');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateCreditMemo = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/otc/returns/${returnsNumber}/create-credit-memo`
      );
      toast.success(`Credit Memo '${response.data.credit_memo_number}' created`);
      navigate(`/otc/credit-memos/${response.data.credit_memo_number}`);
    } catch (error) {
      console.error('Error creating credit memo:', error);
      toast.error(error.response?.data?.detail || 'Failed to create credit memo');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'CREATED': { color: 'bg-orange-100 text-orange-800', icon: Clock },
      'RECEIVED': { color: 'bg-yellow-100 text-yellow-800', icon: RotateCcw },
      'INSPECTED': { color: 'bg-orange-100 text-orange-800', icon: CheckCircle2 },
      'CREDITED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'CANCELLED': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: FileText };
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${badge.color}`}>
        <Icon className="w-4 h-4" /> {status}
      </span>
    );
  };

  const getInspectionBadge = (status) => {
    const colors = {
      'PENDING': 'bg-gray-100 text-gray-700',
      'RECEIVED': 'bg-yellow-100 text-yellow-700',
      'APPROVED': 'bg-green-100 text-green-700',
      'REJECTED': 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${colors[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading returns order...</span>
        </div>
      </Layout>
    );
  }

  if (!returns) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Returns Order Not Found</h1>
          <Button onClick={() => navigate('/otc/returns')}>Back to Returns List</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="returns-order-view">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/returns')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <RotateCcw className="w-6 h-6" />
                Returns Order {returns.returns_number}
              </h1>
              <p className="text-sm text-gray-600">Document Type: {returns.document_type}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(returns.status)}
          </div>
        </div>

        {/* Status Flow & Actions */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['CREATED', 'RECEIVED', 'INSPECTED', 'CREDITED'].includes(returns.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>1</span>
              <span className="text-sm font-medium">Created</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['RECEIVED', 'INSPECTED', 'CREDITED'].includes(returns.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>2</span>
              <span className="text-sm font-medium">Received</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['INSPECTED', 'CREDITED'].includes(returns.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>3</span>
              <span className="text-sm font-medium">Inspected</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                returns.status === 'CREDITED' ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>4</span>
              <span className="text-sm font-medium">Credit Memo</span>
            </div>
            
            <div className="flex gap-2">
              {returns.status === 'CREATED' && (
                <Button onClick={handleReceiveReturns} disabled={isProcessing}>
                  {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
                  Receive Goods
                </Button>
              )}
              {returns.status === 'RECEIVED' && (
                <Button onClick={handleApproveForCredit} disabled={isProcessing}>
                  {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Approve for Credit
                </Button>
              )}
              {returns.status === 'INSPECTED' && !returns.credit_memo_number && (
                <Button onClick={handleCreateCreditMemo} disabled={isProcessing} className="bg-green-600 hover:bg-green-700">
                  {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ReceiptText className="w-4 h-4 mr-2" />}
                  Create Credit Memo
                </Button>
              )}
              {returns.credit_memo_number && (
                <Button variant="outline" onClick={() => navigate(`/otc/credit-memos/${returns.credit_memo_number}`)}>
                  <FileText className="w-4 h-4 mr-2" />
                  View Credit Memo
                </Button>
              )}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Header Info */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Return Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Customer</span>
                <span className="font-medium">{returns.customer_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Return Date</span>
                <span className="font-medium">{returns.return_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Return Reason</span>
                <span className="font-medium">{returns.return_reason}</span>
              </div>
              {returns.return_note && (
                <div>
                  <span className="text-gray-600">Note:</span>
                  <p className="text-sm mt-1">{returns.return_note}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Reference */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Reference</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Original SO</span>
                <button
                  onClick={() => navigate(`/otc/sales-orders/${returns.reference_so_number}`)}
                  className="font-mono text-orange-600 hover:underline"
                >
                  {returns.reference_so_number}
                </button>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sales Org</span>
                <span className="font-medium">{returns.sales_organization}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Dist. Channel</span>
                <span className="font-medium">{returns.distribution_channel}</span>
              </div>
              {returns.credit_memo_number && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Credit Memo</span>
                  <button
                    onClick={() => navigate(`/otc/credit-memos/${returns.credit_memo_number}`)}
                    className="font-mono text-green-600 hover:underline"
                  >
                    {returns.credit_memo_number}
                  </button>
                </div>
              )}
            </div>
          </Card>

          {/* Values */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Values
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Return Value</span>
                <span className="font-mono font-medium">
                  ${returns.total_return_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Credit Value</span>
                <span className="font-mono font-medium text-green-600">
                  ${returns.total_credit_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Currency</span>
                <span className="font-medium">{returns.currency}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Items Table */}
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" /> Return Items
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Return Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Received</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Credit Qty</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reason</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Net Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {returns.items?.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono">{item.item_number.toString().padStart(4, '0')}</td>
                    <td className="px-4 py-3 font-mono text-sm">{item.material_id || '-'}</td>
                    <td className="px-4 py-3">{item.item_description}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {item.return_quantity} {item.unit_of_measure}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {returns.status === 'CREATED' ? (
                        <Input
                          type="number"
                          min="0"
                          max={item.return_quantity}
                          value={receiveQuantities[item.item_number] || 0}
                          onChange={(e) => setReceiveQuantities({
                            ...receiveQuantities,
                            [item.item_number]: parseFloat(e.target.value) || 0
                          })}
                          className="w-20 text-right"
                        />
                      ) : (
                        <span className="font-mono">{item.received_quantity}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {returns.status === 'RECEIVED' ? (
                        <Input
                          type="number"
                          min="0"
                          max={item.received_quantity}
                          value={approveQuantities[item.item_number] || 0}
                          onChange={(e) => setApproveQuantities({
                            ...approveQuantities,
                            [item.item_number]: parseFloat(e.target.value) || 0
                          })}
                          className="w-20 text-right"
                        />
                      ) : (
                        <span className="font-mono text-green-600">{item.credit_quantity}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getInspectionBadge(item.inspection_status)}
                    </td>
                    <td className="px-4 py-3 text-sm">{item.return_reason}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${item.net_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Timestamps */}
        <div className="mt-6 text-sm text-gray-500">
          <p>Created: {returns.created_at ? new Date(returns.created_at).toLocaleString() : '-'}</p>
          <p>Updated: {returns.updated_at ? new Date(returns.updated_at).toLocaleString() : '-'}</p>
        </div>
      </div>
    </Layout>
  );
};

export default ReturnsOrderView;
